import i18n from '../../../i18n';
import { POINT_TOLERANCE_DIAMETER_IN_MM, NG_LAYER_TYPES, RELATIONSHIP_TYPES, OWS_TYPE } from '../../../constants';
import { arraySortAscendingFunction, stringNotEmpty } from '../../../services/utilities';
import { extendThemeLayer, nonGeoServerFilterLimitationFix } from '../../../services/map/layer';
import { getFeatureProperties } from '../../../services/selections';
import { prepareLayer } from '../../../services/map';
import { getNedglobeApi } from '../../../services/api/nedglobe';

export const actions = {
    async addImportLayer(store, importLayer) {
        // In addition to theme-, feedback-, base-layers there are also custom layers,
        // which are temporary imported layers (see constant NG_LAYER_TYPES).
        // Temporary imported layers (NG_LAYER_TYPES.CUSTOM) consist of different servicetypes:
        // WFS/WMS (web service) and features (via imported data from file).

        let layerData;
        if (importLayer.serviceType !== OWS_TYPE.WFS && importLayer.serviceType !== OWS_TYPE.WMS) {
            const olLayer = await prepareLayer(store, null, {
                type: NG_LAYER_TYPES.CUSTOM,
                data: { data: importLayer.data },
                extended: {
                    serviceType: importLayer.serviceType, computedStyles: importLayer.computedStyles
                }
            });
            const features = olLayer.getSource().getFeatures();
            let usesTolerance = false;
            const geometryType = [...new Set(features
                .map(feature => feature.getGeometry().getType())
                .map(geometryTypeName => {
                    switch (geometryTypeName) {
                        case 'Point':
                        case 'MultiPoint':
                            usesTolerance = true;
                            return 'P';
                        case 'LineString':
                        case 'MultiLineString':
                            usesTolerance = true;
                            return 'L';
                        case 'LinearRing':
                        case 'Polygon':
                        case 'MultiPolygon':
                        case 'GeometryCollection':
                        case 'Circle':
                        default:
                            return 'V';
                    }
                })
            )].join('');
            const tolerance = usesTolerance ? POINT_TOLERANCE_DIAMETER_IN_MM : 0;
            const informationAttributes = getFeatureProperties(features);
            layerData = {
                id: importLayer.id,
                data: features,
                name: importLayer.name,
                title: importLayer.name,
                geometryType,
                styles: importLayer.styles,
                serviceType: 'features',
                informationAttributes,
                tolerance
            };
        } else {
            layerData = {
                ...importLayer,
                title: importLayer.name,
                geometryType: 'PLV',
                source: {
                    uri_WFS: null,
                    uri_WMS: null,
                    uri_WMTS: null
                },
                getCombinedOlId: function () {
                    return null;
                },
                customEndPoints: {
                    legend: { url: importLayer.legendGraphicUrl }
                },
                tiledRequest: false,
                format: 'image/png'
            };
        }

        const layer = {
            type: NG_LAYER_TYPES.CUSTOM,
            layerData,
            active: false,
            extended: {
                ...layerData,
                exportable: true,
                computedStyles: importLayer.computedStyles,
                minResolution: importLayer.computedStyles.minResolution,
                maxResolution: importLayer.computedStyles.maxResolution,
            },
            filters: { settings: [] },
            relations: null
        };

        store.commit('ADD_LAYER', layer);
    },
    async activateLayer({ dispatch }, { layer, mapName }) {
        const layerType = layer.type;
        if (layerType === 'theme') {
            const themeId = layer.themeId;
            await dispatch('toggleThemeLayer', { mapName, themeId, id: layer.themeLayerId, active: true });
        } else if (layerType === 'feedback') {
            const feedbackId = layer.feedbackId;
            dispatch('toggleFeedback', { mapName, feedbackId, active: true });
        }
    },
    checkUsermapFilters({ commit }, { userMaps, layer }) {
        // check all usermaps
        userMaps.forEach((u) => {
            // check every filter in this usermap
            let existingFilters = [];
            let filtersChanged = false;
            u.content?.filters?.forEach((uf) => {
                if (uf.layerId === layer.layerData.customId) {
                    const filter = layer.filters.data.find(f => f.settings.name === uf.filter.name
                        && uf.filter.informationAttributeId === f.settings.informationAttributeId
                        && f.settings.visible === true);

                    // Non visible filters settings will not be used from the usermap.
                    // Visible filters must be updated with dashboard settings
                    if (filter) {
                        const newfilter = { ...filter.settings };
                        // value, selected
                        newfilter.value = uf.filter.value;
                        newfilter.selected = uf.filter.selected;

                        // If filter.operatorEditable is true we also copy the userMap operator setting.
                        if (filter.operatorEditable === true) {
                            newfilter.operator = uf.operator;
                        }

                        uf.filter = newfilter;
                        existingFilters.push(uf);
                    } else {
                        // Mark that filter is not added to existingFilters.
                        // Need to store change at the end.
                        filtersChanged = true;
                    }
                } else {
                    // Add filters belonging to other layers.
                    existingFilters.push(uf);
                }
            });
            if (filtersChanged) {
                // Save existingFilters to usermap.
                commit('SET_USERMAP_FILTERS', { id: u.id, filters: existingFilters });
            }
        });
    },
    async addLayer(store, layerData) {
        const layer = { ...layerData };

        const service = await getNedglobeApi(store);
        service.withServer({ url: window.location.origin, description: 'origin' });
        const stylename = layer.layerData?.styles?.[0]?.name;
        layer.layerData.customEndPoint = store.getters.endpoint;
        layer.layerData.customEndPoints = {
            wfs: service.getRequestConfigForOperation('GetWebFeatureByLayerId', [layer.layerData.id]),
            wms: service.getRequestConfigForOperation('GetWebMapByLayerId', [layer.layerData.id]),
            wmts: service.getRequestConfigForOperation('GetWebMapTileByLayerId', [layer.layerData.id]),
            legend: stylename
                ? service.getRequestConfigForOperation('GetLegendByStyle', [{ Id: layer.layerData.id, Style: window.btoa(stylename) }])
                : service.getRequestConfigForOperation('GetLegend', [{ Id: layer.layerData.id }])
        };

        if (layer.type === 'theme' || layer.type === 'feedback') {
            const parentId = layer.type === 'feedback' ? layer.layerData.feedbackId : layer.layerData.themeId;
            const recievedFilters = await store.dispatch('getThemeLayerAttributeFilters', { themeId: parentId, LayerId: layer.layerData.id });
            const filters = nonGeoServerFilterLimitationFix(layer.layerData, recievedFilters);
            layer.filters = { data: filters, settings: [] };
            let userMaps = store.state.userMaps;

            // remove filters from usermap that don't exist in theme anymore.
            if (userMaps) {
                store.dispatch('checkUsermapFilters', { userMaps: userMaps, layer: layer });
            }
        }

        if (layer.type === 'theme') {
            layer.relations = await store.dispatch('getAllRelations', { themeId: layer.layerData.themeId, layerId: layer.layerData.id });
        }

        const extendedLayer = await extendThemeLayer(store, layer);
        store.commit('ADD_LAYER', { ...layer, extended: extendedLayer, active: false });

        if (layer.type === 'theme') {
            store.dispatch('setFilters', { layer, updateMap: layerData.updateMap });
        }
    },

    async getAllRelations(store, { themeId, layerId }) {
        // ThemeLayer relations
        let relationsDataResult = null;
        if (store.getters.isMobile) {
            relationsDataResult = await store.dispatch('useApiData', {
                name: 'GetThemeLayerRelations',
                parameters: {
                    themeId,
                    themeLayerId: layerId,
                    dashboard: false
                }
            });
        } else {
            relationsDataResult = await store.dispatch('useApiData', {
                name: 'WebGetThemeLayerRelations',
                parameters: {
                    id: store.getters.getWebTheme.id,
                    themeLayerId: layerId
                }
            });
        }
        const relationList = [...(relationsDataResult?.data ?? [])].map((relation) => {
            return {
                ...relation,
                type: RELATIONSHIP_TYPES.ThemeLayer2ThemeLayer
            };
        });

        // Table relations only available if user is authorized in mobile
        let relationTableList = [];
        let endPoint = '';

        if (store.getters.isMobile) {
            endPoint = 'GetAuthorizedThemeLayerTableRelations';
        } else {
            endPoint = 'GetPublicThemeLayerTableRelations';
        }
        const relationsTableDataResult = await store.dispatch('useApiData', { name: endPoint, parameters: { Id: layerId } });
        relationTableList = [...(relationsTableDataResult?.data ?? [])].map((relation) => {
            if (typeof relation.settings === 'string') {
                return {
                    ...relation,
                    type: RELATIONSHIP_TYPES.ThemeLayer2Table,
                    settings: JSON.parse(relation.settings),
                    targetThemeLayerId: relation.toTableSearchResultId
                };
            }
            return relation;
        });

        return [...relationList, ...relationTableList].sort(
            arraySortAscendingFunction('name')
        );
    },

    setLayerErrorState({ commit, getters, dispatch }, { layerId, error }) {
        const layer = getters.getLayerById(layerId);

        // If layer cannot be found then don't do any further actions on the layer
        // because if you do, the application will fail.
        if (layer) {
            if (error) {
                let message;
                if (layer.type === 'feedback') {
                    message = `${i18n.t('layerLoadError', [layer.data.title])}`;
                    dispatch('toggleFeedback', { mapName: 'main', feedbackId: layer.data.feedbackId, active: false });
                } else {
                    message = `${i18n.t('layerLoadError', [layer.data.title])}`;
                    dispatch('toggleThemeLayer', { mapName: 'main', themeId: layer.data.themeId, id: layer.data.id, active: false });
                }
                commit('ADD_STATUS_MESSAGE', {
                    message,
                    variant: 'warning',
                    autoHideDelay: 5000
                });
            }
            commit('SET_LAYER_ERROR_STATE', { id: layerId, error });
        }
    },

    setLayerUnAuthorizedState({ commit, getters, dispatch }, { layerId }) {
        const layer = getters.getLayerById(layerId);

        // If the user is unauthorized for this layer then don't do any further actions on the layer
        if (layer) {
            let message = `${i18n.t('layerUserUnauthorized', [layer.data.title])}`;
            dispatch('toggleThemeLayer', { mapName: 'main', themeId: layer.data.themeId, id: layer.data.id, active: false });

            commit('ADD_STATUS_MESSAGE', {
                message,
                variant: 'warning',
                autoHideDelay: 5000
            });
        }
    },

    setLayerCheckedState({ commit }, { layerId, checked }) {
        commit('SET_LAYER_CHECKED_STATE', { id: layerId, checked });
    },

    setSourceWfsOutPutFormat({ commit }, { sourceId, format }) {
        commit('SET_SOURCE_WFS_OUTPUT_FORMAT', { id: sourceId, format });
    },

    setSourceWmsOutPutFormat({ commit }, { sourceId, format }) {
        commit('SET_SOURCE_WMS_OUTPUT_FORMAT', { id: sourceId, format });
    },

    /**
     * WFS GetFeature request
     */
    requestLayerFeatures({ dispatch }, { data, useLoader, layerId }) {
        return dispatch('useApiData', {
            name: 'GetWebFeatureByLayerId',
            data,
            useLoader,
            parameters: { id: layerId },
            config: { headers: { 'Content-Type': 'application/xml' } }
        });
    },

    /**
     * WMS GetFeatureInfo request (point only)
     */
    requestLayerFeatureInfo({ dispatch }, { data, useLoader }) {
        return dispatch('useApiData', {
            name: 'GetFeatureInfoByLayerId',
            data,
            useLoader,
            config: { headers: { 'Content-Type': 'application/json' } }
        });
    },

    requestBagServiceFeatures({ dispatch }, { data, useLoader }) {
        return dispatch('useApiData', {
            name: 'GetBagFeatureByFilter',
            data,
            useLoader,
            config: { headers: { 'Content-Type': 'application/xml' } }
        });
    },
    async setLabelAttributes({ commit, dispatch }, { layerId, attributes }) {
        // Store label info in layer.extended.informationattributes
        commit('SET_ATTRIBUTES_AS_LABEL', {
            layerId: layerId,
            attributes: attributes
        });

        // activate the label layer
        await dispatch('activateLabelLayer', { layerId: layerId });
    },
    async activateLabelLayer({ commit, dispatch, getters }, { layerId }) {
        // If layer has no extra layer for labels we need to add it if it now has labels.
        const layer = getters.getLayerById(layerId);
        const hasLabels = layer.extended.informationAttributes.some(i => i.isTextAttribute);

        // This will be true if the layer has a graphical layer and has labels.
        let newHasGraphicalLayerAndStyleTextAttribute = (layer.extended.graphicalLayerName?.length > 0 &&
            (stringNotEmpty(layer.extended.computedStyles[0]?.settings['default']?.textAttribute)
                || hasLabels));

        //  If either is true this means labels have changed and there is a graphical layer
        if (layer.extended.hasGraphicalLayerAndStyleTextAttribute != newHasGraphicalLayerAndStyleTextAttribute) {
            // Store the new value and create extra layer if necessary
            commit('SET_HAS_LABELS', { layerId: layerId, hasLabels: newHasGraphicalLayerAndStyleTextAttribute });

            const mapConfig = getters.getMapByName('main');
            if (!mapConfig) {
                throw new Error('map not found');
            }
            const map = mapConfig.map;
            if (!layer.extended.hasGraphicalLayerAndStyleTextAttribute && newHasGraphicalLayerAndStyleTextAttribute) {
                await dispatch('createExtraWfsLayer', {
                    mapName: 'main',
                    map,
                    layerObject: layer,
                    id: layerId,
                    highQuality: true,
                    minResolution: layer.extended.minResolution,
                    maxResolution: layer.extended.maxResolution,
                    textMaxResolution: layer.extended.textMaxResolution
                });
            }
        }

        await dispatch('addLayerToMap', {
            mapName: null,
            id: layerId
        });
    },
    async loadLayers3D({ dispatch, commit, state }) {
        let loadedLayers3D = await dispatch('useApiData', { name: 'GetAuthorizedLayers3D' });
        commit('ADD_LAYERS_3D', { layers3D: loadedLayers3D.data });
        return loadedLayers3D.data;
    },
};
